import type { IntlShape } from 'react-intl-next';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Modal, { ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { fg } from '@confluence/feature-gating';

import { i18n } from '../confluenceDatabasesExtensionI18n';
import { useInitialUrl } from '../hooks/useInitialUrl';
import { DatabaseReferenceModalCurrentUserQuery } from '../DatabaseReferenceModalCurrentUser.graphql';
import type { DatabaseReferenceModalCurrentUserQuery as DatabaseReferenceModalCurrentUserQueryType } from '../__types__/DatabaseReferenceModalCurrentUserQuery';

import { LegacyDatabaseReferenceModalSkeleton } from './LegacyDatabaseReferenceModalSkeleton';
import { DatabaseReferenceModalSkeleton } from './DatabaseReferenceModalSkeleton';

export type DatabaseReferenceModalProps = {
	onClose: (result: string | undefined) => void;
	intl: IntlShape;
};

type NullableString = string | null | undefined;

type Identity = {
	accountId: NullableString;
	nickname: NullableString;
	picture: NullableString;
	email: NullableString;
	locale: NullableString;
};

const isDatabaseUrl = (value: string): boolean => /database\/\d+/.test(value);

export const DatabaseReferenceModal = ({ onClose, intl }: DatabaseReferenceModalProps) => {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [identity, setIdentity] = useState<Identity | null>(null);
	const { activationId, userId, cloudId } = useSessionData();
	const initialUrl = useInitialUrl();
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		getApolloClient()
			.query({
				query: DatabaseReferenceModalCurrentUserQuery,
			})
			.then(({ data }: { data: DatabaseReferenceModalCurrentUserQueryType }) => {
				setIdentity({
					accountId: data?.user?.id,
					nickname: data?.user?.displayName,
					picture: data?.user?.photos?.find((photo) => photo?.isPrimary)?.value,
					email: data?.user?.emails?.find((email) => email?.isPrimary)?.value,
					locale: data?.user?.locale,
				});
			});
	}, []);

	const bridgeData = {
		cloudId,
		activationId,
		userId,
		identity,
	};

	const messageCallback = useCallback(
		(event: MessageEvent) => {
			const isFromExpectedIframe =
				iframeRef.current && event.source === iframeRef.current.contentWindow;

			if (isFromExpectedIframe) {
				try {
					const data = JSON.parse(event.data);
					if (data.finalDatabaseUrl && isDatabaseUrl(data.finalDatabaseUrl)) {
						onClose(data.finalDatabaseUrl);
					} else if (data.cancelDatabaseReference) {
						onClose(undefined);
					} else if (data.databaseReferencingComponentLoaded) {
						setIsLoading(false);
					}
				} catch (error) {}
			}
		},
		[onClose, iframeRef],
	);

	useEffect(() => {
		window.addEventListener('message', messageCallback);

		return () => {
			window.removeEventListener('message', messageCallback);
		};
	}, [messageCallback]);

	const SkeletonComponent = fg('databases-frontend-embed-database-modal')
		? DatabaseReferenceModalSkeleton
		: LegacyDatabaseReferenceModalSkeleton;

	return (
		<ModalTransition>
			<Modal
				width={1200}
				height={592}
				onClose={() => onClose(undefined)}
				testId="reference-database-modal"
				label={intl.formatMessage(i18n.referenceDatabaseModalLabel)}
			>
				{fg('databases-frontend-embed-database-modal') && (
					<ModalHeader hasCloseButton>
						<ModalTitle>{intl.formatMessage(i18n.referenceDatabaseTitle)}</ModalTitle>
					</ModalHeader>
				)}

				{isLoading || !identity ? <SkeletonComponent /> : null}
				<iframe
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						width: '100%',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						height: '600px',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						border: '0px',
						display: !isLoading && identity ? 'block' : 'none',
					}}
					src={initialUrl}
					{...(identity ? { 'data-bridge-data': JSON.stringify(bridgeData) } : {})}
					title="reference-database-frame"
					ref={iframeRef}
				/>
			</Modal>
		</ModalTransition>
	);
};
